/**
 * Formatowanie liczb i dat.
 *
 * Backend wysyła wyłącznie `*_minor` (grosze) razem z kodem waluty — całe
 * zaokrąglanie i skracanie dzieje się dopiero tutaj, bo tylko interfejs wie,
 * czy w danym miejscu mieści się „12 340,00 zł", czy „12,3 tys. zł".
 */

import type { MetricValue } from "./types";

const NBSP = " ";

export function money(minor: number, currency = "PLN", options: { compact?: boolean } = {}) {
  const value = minor / 100;
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    notation: options.compact && Math.abs(value) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: options.compact && Math.abs(value) >= 10_000 ? 1 : 0,
  }).format(value);
}

export function moneyExact(minor: number, currency = "PLN") {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(minor / 100);
}

export function count(value: number) {
  return new Intl.NumberFormat("pl-PL").format(value);
}

export function percent(value: number, digits = 1) {
  return new Intl.NumberFormat("pl-PL", {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Zmiana względna ze znakiem — zawsze z jawnym „+", bo brak znaku czyta się jako spadek. */
export function delta(current: number, previous: number): { text: string; direction: -1 | 0 | 1 } {
  if (!previous) return { text: "—", direction: 0 };
  const change = (current - previous) / Math.abs(previous);
  const direction = change > 0.0005 ? 1 : change < -0.0005 ? -1 : 0;
  const sign = direction > 0 ? "+" : "";
  return { text: `${sign}${percent(change, 1)}`, direction };
}

export function ratio(value: number, digits = 1) {
  return `${new Intl.NumberFormat("pl-PL", { maximumFractionDigits: digits }).format(value)}×`;
}

/**
 * Wyświetlenie metryki, która może nie istnieć.
 *
 * To jest miejsce, w którym reguła „brak danych to nie zero" staje się
 * widoczna dla użytkownika: zamiast „0%" pokazujemy kreskę i powód.
 */
export function metric(
  value: MetricValue | undefined,
  render: (raw: number) => string,
): { text: string; muted: boolean; title?: string } {
  if (!value || !value.available || value.value === null) {
    return { text: "—", muted: true, title: value?.reason ?? "brak danych" };
  }
  return { text: render(value.value), muted: false, title: value.reason ?? undefined };
}

const DATE = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
const DATE_SHORT = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit" });
const DATETIME = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Tekst z API jako `Date`, przy czym sama data `RRRR-MM-DD` to północ czasu lokalnego.
 *
 * `new Date("2026-09-24")` czyta gołą datę jako północ UTC. W Polsce to przypadkiem
 * działa, ale na zachód od Greenwich lokalnie wypada jeszcze 23 września i termin
 * cofa się o dzień. Znacznik czasu z godziną niesie własną strefę, więc idzie wprost.
 */
function parse(value: string): Date {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!day) return new Date(value);
  return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
}

export function date(value: string | null | undefined) {
  return value ? DATE.format(parse(value)) : "—";
}

export function dateShort(value: string) {
  return DATE_SHORT.format(parse(value));
}

export function dateTime(value: string | null | undefined) {
  return value ? DATETIME.format(new Date(value)) : "—";
}

/**
 * Termin względem dzisiaj: „dziś", „jutro", „2 dni po terminie".
 *
 * Sama data wymaga liczenia w pamięci przy każdej karcie na tablicy, a
 * pytanie brzmi zawsze tak samo: czy to już przeterminowane, czy jeszcze nie.
 */
export function due(value: string | null | undefined): { text: string; overdue: boolean } | null {
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = parse(value);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);

  if (days === 0) return { text: "dziś", overdue: false };
  if (days === 1) return { text: "jutro", overdue: false };
  if (days === -1) return { text: "wczoraj", overdue: true };
  if (days < 0) return { text: `${-days}${NBSP}dni po terminie`, overdue: true };
  if (days <= 14) return { text: `za${NBSP}${days}${NBSP}dni`, overdue: false };
  return { text: dateShort(value), overdue: false };
}

/** „3 h temu", „wczoraj” — do świeżości danych, gdzie liczy się wiek, nie data. */
export function ago(value: string | null | undefined) {
  if (!value) return "nigdy";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60_000);
  if (minutes < 2) return "przed chwilą";
  if (minutes < 60) return `${minutes}${NBSP}min temu`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours}${NBSP}h temu`;
  return `${Math.round(hours / 24)}${NBSP}dni temu`;
}

export const LABELS = {
  productStatus: {
    idea: "pomysł",
    building: "w budowie",
    live: "produkcja",
    maintenance: "utrzymanie",
    sunsetting: "wygaszany",
    archived: "archiwum",
  } as Record<string, string>,
  customerStatus: {
    lead: "lead",
    trialing: "trial",
    active: "aktywny",
    past_due: "zaległość",
    cancelled: "wypowiedziana",
    churned: "utracony",
  } as Record<string, string>,
  movement: {
    new: "nowy",
    expansion: "rozwój",
    contraction: "obniżka",
    churn: "utrata",
    reactivation: "powrót",
  } as Record<string, string>,
  severity: { info: "informacja", warning: "ostrzeżenie", critical: "krytyczny" } as Record<
    string,
    string
  >,
  source: {
    product_events: "zdarzenia produktu",
    product_state_sync: "synchronizacja stanu",
    ga4: "Google Analytics 4",
    gsc: "Search Console",
    google_ads: "Google Ads",
    meta_ads: "Meta Ads",
  } as Record<string, string>,
  taskStatus: {
    backlog: "backlog",
    todo: "do zrobienia",
    doing: "w toku",
    review: "przegląd",
    done: "zrobione",
    cancelled: "odrzucone",
  } as Record<string, string>,
  taskType: {
    task: "zadanie",
    bug: "błąd",
    feature_request: "prośba klienta",
    chore: "porządki",
  } as Record<string, string>,
  priority: {
    p0: "P0 · gasimy pożar",
    p1: "P1 · w tym tygodniu",
    p2: "P2 · normalne",
    p3: "P3 · kiedyś",
  } as Record<string, string>,
  role: {
    owner: "właściciel",
    analyst: "analityk",
    viewer: "podgląd",
  } as Record<string, string>,
  notification: {
    task_assigned: "zadanie dla Ciebie",
    subtask_assigned: "podzadanie dla Ciebie",
  } as Record<string, string>,
  emailStatus: {
    skipped: "bez maila — poczta wyłączona",
    pending: "mail w drodze",
    sent: "mail wysłany",
    failed: "mail nie wyszedł",
  } as Record<string, string>,
  initiativeStatus: {
    discovery: "rozpoznanie",
    planned: "zaplanowana",
    in_progress: "w toku",
    shipped: "wdrożona",
    parked: "wstrzymana",
    dropped: "porzucona",
  } as Record<string, string>,
  costCategory: {
    hosting: "hosting",
    domain: "domena",
    tooling: "narzędzia",
    ads: "reklama",
    payment_fees: "prowizje płatności",
    contractor: "podwykonawca",
    accounting: "księgowość",
    equipment: "sprzęt",
    legal: "prawo",
    other: "inne",
  } as Record<string, string>,
  lessonDomain: {
    technical: "techniczny",
    business: "biznesowy",
  } as Record<string, string>,
  lessonScope: {
    product: "ten produkt",
    portfolio: "całe portfolio",
    organization: "sposób pracy",
  } as Record<string, string>,
  lessonStatus: {
    open: "zapisany",
    accepted: "wniosek obowiązuje",
    archived: "archiwum",
  } as Record<string, string>,
  healthComponent: {
    mrr_growth: "wzrost MRR",
    customer_churn: "churn klientów",
    revenue_churn: "churn przychodu",
    net_revenue_retention: "retencja przychodu",
    trial_conversion: "konwersja triali",
    activation_rate: "aktywacja",
    ltv_cac: "LTV / CAC",
    organic_growth: "wzrost organiczny",
  } as Record<string, string>,
};
